import { cva, type VariantProps } from "class-variance-authority";

import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

const loadingStateVariants = cva("flex items-center justify-center text-muted-foreground", {
  variants: {
    size: {
      default: "h-full",
      sm: "h-32",
      lg: "h-full min-h-[400px]",
    },
    layout: {
      center: "flex-col gap-2",
      inline: "flex-row gap-2",
    },
  },
  defaultVariants: {
    size: "default",
    layout: "center",
  },
});

interface LoadingStateProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingStateVariants> {
  /** Message to display below the spinner */
  message?: string;
  /** Whether to show the spinner icon */
  showSpinner?: boolean;
}

/**
 * LoadingState - A consistent loading indicator for async data states.
 *
 * Use this when:
 * - Convex queries return `undefined` (still loading)
 * - Fetching data from external APIs
 * - Any async operation in progress
 *
 * @example
 * ```tsx
 * // Basic usage (full height container)
 * if (data === undefined) {
 *   return <LoadingState message="Loading dataset..." />;
 * }
 *
 * // Smaller inline loading
 * <LoadingState size="sm" message="Fetching..." />
 *
 * // With spinner
 * <LoadingState showSpinner message="Processing..." />
 * ```
 */
function LoadingState({
  className,
  size,
  layout,
  message = "Loading...",
  showSpinner = false,
  ...props
}: LoadingStateProps) {
  return (
    <div className={cn(loadingStateVariants({ size, layout, className }))} {...props}>
      {showSpinner && <Spinner className="size-5" />}
      <p className="text-sm">{message}</p>
    </div>
  );
}

/**
 * LoadingPanel - a centered spinner filling the box it is given.
 *
 * The wordless counterpart to `LoadingState`: use it where the heading above already says what is
 * loading (a card body, a table's placeholder row, a dialog section) and a "Loading..." sentence
 * would only repeat it. The caller owns the box — minimum height, border and background arrive
 * through `className`: `<LoadingPanel className="min-h-24 border bg-background" />`.
 */
function LoadingPanel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex items-center justify-center", className)} {...props}>
      <Spinner className="size-5 text-primary" />
    </div>
  );
}

interface LoadingRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** What is loading, as a sentence. It is the only thing naming the in-flight work. */
  message: React.ReactNode;
}

/**
 * LoadingRow - a left-aligned spinner followed by what is loading.
 *
 * The reading-flow sibling of `LoadingPanel`: an admin panel body or a table area that replaces
 * its content with "Loading runs..." while a query resolves. `LoadingState` is the wrong shape
 * there — it centers its message in a full-height box — so this row starts at the left margin and
 * takes only the padding of the box it sits in through `className`:
 * `<LoadingRow className="p-5" message="Loading runs..." />`.
 */
function LoadingRow({ message, className, ...props }: LoadingRowProps) {
  return (
    <div
      className={cn("flex items-center gap-2 text-muted-foreground text-sm", className)}
      {...props}
    >
      <Spinner className="size-4" />
      {message}
    </div>
  );
}

export { LoadingState, LoadingPanel, LoadingRow, loadingStateVariants };
